import { DEFAULT_COUNTRY_CODE } from '../configs/connect';

// Phone-number helpers for the Amazon Connect softphone. Streams' agent.connect
// requires E.164 (`+<country><subscriber>`); we accept relaxed user input and
// normalise it. Display formatting is locale-aware only for AU numbers — all
// other formats pass through unchanged.

const DIGITS_ONLY = /\D+/g;

/**
 * Normalise loose user input to E.164. Returns null if the result couldn't
 * possibly be a dialable number (too short / non-numeric noise only).
 *
 * Rules:
 *  - Already starts with `+` → keep, strip non-digits after the `+`.
 *  - Starts with `00` → international prefix, replace with `+`.
 *  - Starts with `0` → assume the configured country code (default AU).
 *  - Otherwise digits-only → assume the configured country code.
 */
export const toE164 = (input: string): string | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('+')) {
    const digits = trimmed.slice(1).replace(DIGITS_ONLY, '');
    return digits.length >= 6 ? `+${digits}` : null;
  }

  const digits = trimmed.replace(DIGITS_ONLY, '');
  if (digits.length < 4) return null;

  if (digits.startsWith('00')) {
    const rest = digits.slice(2);
    return rest.length >= 6 ? `+${rest}` : null;
  }

  if (digits.startsWith('0')) {
    return `${DEFAULT_COUNTRY_CODE}${digits.slice(1)}`;
  }

  return `${DEFAULT_COUNTRY_CODE}${digits}`;
};

/**
 * Format an E.164 number for display. AU mobiles render as `04XX XXX XXX`
 * and AU landlines as `(0X) XXXX XXXX`. Everything else is returned with a
 * single space after the country code so the country prefix stays readable.
 */
export const formatForDisplay = (e164: string | null): string => {
  if (!e164) return 'Unknown';
  if (!e164.startsWith('+')) return e164;

  if (e164.startsWith('+61')) {
    const local = `0${e164.slice(3)}`;
    if (local.startsWith('04') && local.length === 10) {
      return `${local.slice(0, 4)} ${local.slice(4, 7)} ${local.slice(7)}`;
    }
    if (local.length === 10) {
      return `(${local.slice(0, 2)}) ${local.slice(2, 6)} ${local.slice(6)}`;
    }
  }

  // Generic international: split the country prefix off so it's readable.
  // Country prefixes are 1–3 digits; bias toward 2 to match common cases.
  const cc = e164.slice(0, 3);
  const rest = e164.slice(3);
  return `${cc} ${rest}`;
};

/**
 * Format a duration in seconds as `mm:ss` (or `h:mm:ss` for long calls).
 * Used by the call history list and the active-call timer.
 */
export const formatDuration = (seconds: number | null): string => {
  if (seconds === null || seconds < 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};
